/**
 * TourContainer - App-Shell level tour mounting
 * 
 * This component mounts the FirstTimeAppTour at the App level,
 * NOT inside route-dependent components. This prevents the tour
 * from being unmounted when routes change.
 * 
 * SAFETY: If onboarding state is 'tour' but tour hasn't rendered
 * within 10 seconds, force-complete the tour to prevent deadlock.
 */

import React, { useEffect, useState, useRef } from 'react';
import { FirstTimeAppTour } from './FirstTimeAppTour';
import { getOnboardingState, isHydrated, completeTour } from '@/hooks/useOnboardingStateMachine';
import { isTourActiveGlobal, isTourSeen } from '@/hooks/useTourController';
import { logFlight } from '@/hooks/useOnboardingStateMachine';

const TOUR_DEADLOCK_TIMEOUT_MS = 15_000;

export function TourContainer() {
  const [shouldRenderTour, setShouldRenderTour] = useState(false);
  const deadlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  useEffect(() => {
    const checkTourRender = () => {
      const state = getOnboardingState();
      const hydrated = isHydrated();
      const tourActive = isTourActiveGlobal();
      const tourAlreadySeen = isTourSeen();
      
      // SAFETY: If state says 'tour' but tour is already marked seen,
      // force-complete to prevent deadlock
      if (hydrated && state === 'tour' && tourAlreadySeen) {
        logFlight('TOUR_CONTAINER_DEADLOCK_RECOVERY', { state, tourAlreadySeen });
        completeTour();
        setShouldRenderTour(false);
        return;
      }
      
      const shouldRender = (hydrated && (state === 'tour' || state === 'app')) || tourActive;
      
      if (shouldRender !== shouldRenderTour) {
        logFlight('TOUR_CONTAINER_RENDER_CHANGE', { shouldRender, state, hydrated, tourActive });
        setShouldRenderTour(shouldRender);
      }
    };
    
    checkTourRender();
    const interval = setInterval(checkTourRender, 500);
    return () => clearInterval(interval);
  }, [shouldRenderTour]);
  
  // SAFETY: Deadlock timeout - if tour stage persists without the tour
  // actually starting (isTourActiveGlobal stays false), force-complete after timeout
  useEffect(() => {
    if (!shouldRenderTour) {
      if (deadlockTimerRef.current) clearTimeout(deadlockTimerRef.current);
      return;
    }
    
    deadlockTimerRef.current = setTimeout(() => {
      const state = getOnboardingState();
      const tourActive = isTourActiveGlobal();
      if (state === 'tour' && !tourActive) {
        console.error('THERAPPI_FATAL: Tour deadlock after 15s, auto-completing');
        logFlight('TOUR_CONTAINER_TIMEOUT_RECOVERY', { state, tourActive });
        // Set BOTH completion keys
        try {
          localStorage.setItem('therappi_tour_completed', 'true');
          localStorage.setItem('therappi-first-time-tour-seen', 'true');
        } catch {}
        completeTour();
        setShouldRenderTour(false);
      }
    }, TOUR_DEADLOCK_TIMEOUT_MS);
    
    return () => {
      if (deadlockTimerRef.current) clearTimeout(deadlockTimerRef.current);
    };
  }, [shouldRenderTour]);
  
  if (!shouldRenderTour) {
    return null;
  }
  
  return <FirstTimeAppTour />;
}
